<template>
    <section ref="profileSearch" class="flex flex-col gap-2" aria-labelledby="profile-search-heading" data-testid="link-profile-search">
        <div class="flex items-center justify-between gap-4">
            <h3 id="profile-search-heading" class="scaffold-body-small">{{ t("connections.publicProfileSearch.heading") }}</h3>
            <BaseButton theme="tertiary" size="sm" :disabled="isLinking" @click="dismissSearch">{{ t("connections.publicProfileSearch.notNow") }}</BaseButton>
        </div>

        <div class="flex flex-col gap-3 rounded-lgr border border-border bg-surface-raised p-4">
            <div class="flex flex-col items-stretch gap-3 md:gap-6 sm:flex-row sm:items-center">
                <BaseInput
                    v-model="searchQuery"
                    type="search"
                    :aria-label="t('connections.publicProfileSearch.searchLabel')"
                    :placeholder="t('connections.publicProfileSearch.searchLabel')"
                    :disabled="isLinking">
                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                </BaseInput>
                <PublicProfileRequestLinkAction
                    v-if="profiles.length > 0"
                    :copied="isCopied"
                    hide-dismiss
                    :disabled="isLinking || isCopying"
                    size="sm"
                    class="min-w-40"
                    @copy="copyRequestLink"
                    @dismiss="dismissSearch" />
            </div>

            <BaseNotification v-if="searchError" theme="error" :text="t('connections.publicProfileSearch.searchFailed')" hide-dismiss />
            <BaseNotification v-if="showLinkProfileError" theme="error" :text="t('connections.publicProfileSearch.linkFailed')" @dismiss="linkProfileError = null" />
            <BaseNotification v-if="requestCopyFailed" theme="error" :text="t('connections.publicProfileSearch.copyFailed')" @dismiss="requestCopyFailed = false" />

            <p class="sr-only" role="status">{{ resultStatus }}</p>

            <div v-if="isSearching" class="flex items-center justify-center gap-2 py-10 text-ink-muted">
                <LoadingSpinner class="size-4" aria-hidden="true" />
                <span class="body-small">{{ t("connections.publicProfileSearch.searching") }}</span>
            </div>

            <template v-else-if="filteredProfiles.length > 0">
                <p class="scaffold-details-small text-ink-muted">{{ searchHeading }}</p>
                <ul ref="resultsList" class="divide-y divide-border border-t border-border max-h-80 overflow-auto" :aria-label="t('connections.publicProfileSearch.resultsLabel')">
                    <li v-for="profile in filteredProfiles" :key="profile.id" class="flex items-center gap-3 py-3 pr-3">
                        <CollaborationProfileAvatar size="sm" :name="profile.name" />
                        <span class="flex min-w-0 grow flex-col">
                            <span class="body-small-semibold truncate">{{ profile.name }}</span>
                            <span v-if="subtitleFor(profile)" class="body-small text-ink-muted truncate">{{ subtitleFor(profile) }}</span>
                        </span>
                        <BaseButton
                            theme="primary"
                            size="sm"
                            class="flex gap-2 items-center min-w-18 justify-center"
                            :disabled="isLinking"
                            :aria-disabled="isLinking"
                            :aria-label="
                                t('connections.publicProfileSearch.linkProfileLabel', {
                                    link: t('connections.publicProfileSearch.link'),
                                    name: profile.name ?? t('connections.publicProfileSearch.profileFallbackName'),
                                })
                            "
                            @click="linkSelectedProfile(profile)">
                            <span>{{ t("connections.publicProfileSearch.link") }}</span>
                            <LoadingSpinner v-if="isLinking && linkingProfileUserId === profile.user" class="size-3.5" aria-hidden="true" />
                        </BaseButton>
                    </li>
                    <li v-if="isLoadingMore" class="flex items-center justify-center gap-2 py-3 text-ink-muted">
                        <LoadingSpinner class="size-4" aria-hidden="true" />
                        <span class="body-small">{{ t("connections.publicProfileSearch.loadingMore") }}</span>
                    </li>
                </ul>
            </template>

            <div v-else-if="hasNoMatch" class="flex flex-col items-center gap-3 px-3 py-10 text-center" data-testid="link-profile-no-match">
                <i class="bi bi-person-x text-2xl/none text-ink-muted" aria-hidden="true"></i>
                <h4 class="display-small-serif">{{ t("connections.publicProfileSearch.noMatchTitle", { query: searchedName, organization: organizationLabel }) }}</h4>
                <p class="body-small text-ink-muted">
                    {{ t("connections.publicProfileSearch.noMatchNotOnNadia") }}<br />
                    {{ t("connections.publicProfileSearch.noMatchRequestBody") }}
                </p>
                <PublicProfileRequestLinkAction
                    :copied="isCopied"
                    :disabled="isCopying"
                    :link-copied-text="t('connections.publicProfileSearch.linkCopiedShare')"
                    class="mt-2"
                    @copy="copyRequestLink"
                    @dismiss="dismissSearch" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useInfiniteScroll } from "@vueuse/core";
import type { OrganizationDetails } from "~vue/appTypes";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { logProfileShared, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import { useLinkPublicProfile } from "~vue/composables/useLinkPublicProfile";
import { usePublicProfileSearch } from "~vue/composables/usePublicProfileSearch";
import { useTimedShow } from "~vue/composables/useTimedShow";
import PublicProfileRequestLinkAction from "~vue/Connections/PublicProfileRequestLinkAction.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { ConnectionDict } from "~vue/types/connection";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getFirstName } from "~vue/utils/stringUtils";
import { useFilter } from "reka-ui";
import { computed, inject, nextTick, onMounted, type Ref, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
    connection,
    inviteUrl,
    autoFocus = false,
} = defineProps<{
    connection: ConnectionDict;
    inviteUrl: string;
    autoFocus?: boolean;
}>();

const emit = defineEmits<{ dismiss: [] }>();
const { t } = useI18n();

const profileSearch = useTemplateRef<HTMLElement>("profileSearch");
const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details", ref(null));
const initialSearchTerm = connection.persona.name ?? "";
const {
    query: searchQuery,
    profiles,
    error: searchError,
    isFetching: isSearching,
    hasNoMatch,
    hasMore,
    isLoadingMore,
    loadMore,
} = usePublicProfileSearch({
    initialSearchTerm,
    source: "my_people",
});

const searchedName = computed(() => searchQuery.value.trim());

// Case-insensitive ("accent" sensitivity — reka's own default collator is case-sensitive) match on
// whether every typed word starts some word of the name, so "Levi", "Goertz" and "Levi Goertz" all match.
const { startsWith } = useFilter({ sensitivity: "accent" });
const matchesName = (name: string) => {
    const nameWords = name.trim().split(/\s+/);
    return searchedName.value.split(/\s+/).every((term) => nameWords.some((word) => startsWith(word, term)));
};
const filteredProfiles = computed(() => profiles.value.filter((profile) => matchesName(profile.name ?? "")));

const searchHeading = computed(() =>
    searchedName.value ? t("connections.publicProfileSearch.suggestedMatchesFor", { query: searchedName.value }) : t("connections.publicProfileSearch.suggestedMatches"),
);
const requestFirstName = computed(() => getFirstName(initialSearchTerm) || t("connections.publicProfileSearch.thisPerson"));
const organizationLabel = computed(() => {
    const organization = organizationDetails.value;
    return !organization || organization.is_default || !organization.name ? t("connections.publicProfileSearch.yourCompany") : organization.name;
});

const resultsList = useTemplateRef<HTMLElement>("resultsList");
useInfiniteScroll(resultsList, loadMore, { distance: 80, canLoadMore: () => hasMore.value });

const subtitleFor = (profile: ExistingPublicProfile) => [profile.title, profile.division, profile.email].filter(Boolean).join(" · ");
const resultStatus = computed(() => {
    if (isSearching.value) {
        return t("connections.publicProfileSearch.status.searching");
    }
    if (searchError.value) {
        return t("connections.publicProfileSearch.status.failed");
    }
    if (isLoadingMore.value) {
        return t("connections.publicProfileSearch.status.loadingMore");
    }
    if (filteredProfiles.value.length > 0) {
        return t("connections.publicProfileSearch.status.found", { count: filteredProfiles.value.length }, filteredProfiles.value.length);
    }
    if (hasNoMatch.value) {
        return t("connections.publicProfileSearch.status.noneFound");
    }
    return "";
});

const linkingProfileUserId = ref<string | null>(null);
const {
    linkProfile,
    isLinking,
    error: linkProfileError,
} = useLinkPublicProfile({
    connectionId: computed(() => connection.id),
    source: "my_people",
    onLinked: () => {
        router.reload({
            only: ["connection", "public_profile_user_id", "public_profile_basic_info", "pair_perspective_url", "pair_perspective_page_url", "person_teams_url"],
            onSuccess: async () => {
                await nextTick();
                document.querySelector<HTMLElement>("[data-testid='linked-profile-section']")?.focus();
            },
        });
    },
});

const showLinkProfileError = computed(() => !!linkProfileError.value && !isSessionEndedError(linkProfileError.value));

const linkSelectedProfile = (profile: ExistingPublicProfile) => {
    if (isLinking.value) {
        return;
    }
    linkingProfileUserId.value = profile.user;
    linkProfile(profile.user);
};

const { show: showCopiedRequest, isShowing: isCopied } = useTimedShow({ duration: 10_000 });
const hasCopiedRequest = ref(false);
const isCopying = ref(false);
const requestCopyFailed = ref(false);

const copyRequestLink = async () => {
    if (isCopying.value) {
        return;
    }
    isCopying.value = true;
    requestCopyFailed.value = false;
    try {
        if (!(await copyShareUrl(withShareAttribution(inviteUrl, "my_people", "link"), profileInviteMessage(requestFirstName.value)))) {
            requestCopyFailed.value = true;
            return;
        }
        showCopiedRequest();
        hasCopiedRequest.value = true;
        logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, {
            connection_id: connection.id,
            source: "my_people",
        });
        void logProfileShared({ shareSource: "my_people", target: "link", shareKind: "invite", connectionId: connection.id });
    } finally {
        isCopying.value = false;
    }
};

const dismissSearch = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_SEARCH_CANCELED, {
        connection_id: connection.id,
        copied_invite: hasCopiedRequest.value,
        source: "my_people",
    });
    emit("dismiss");
};

onMounted(async () => {
    if (autoFocus) {
        await nextTick();
        profileSearch.value?.querySelector<HTMLInputElement>("input")?.focus();
    }
});
</script>
